import React, { memo, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { StarIcon } from 'src/ui/icons';
import RatingIcon from 'src/components/rating/smiley-rating/rating-icon';
import Rating from './components/rating';
import { ButtonText, ButtonIcon } from '@prisjakt/ui/button';
import { IconEdit } from '@prisjakt/ui/icon';
import * as S from './styles';
import { SmileyType } from 'src/components/rating/smiley-rating/types';
import { BelongsTo, Store, Review, Product } from '../../areas/reviews/types';
import { getSmileyRating } from 'src/components/rating/smiley-rating/utils';

const SCALE = 5;

type Props = {
  className?: string;
  rating?: number | null;
  count?: number | null;
  countTotal?: number | null;
  distribution?: number[] | null;
  belongsTo?: BelongsTo;
  showPrompt: boolean;
  store?: Store;
  product?: Product;
  myReview?: Review;
  columnView?: boolean;
  showTitles?: boolean;
};

const ReviewsSummary = memo((props: Props) => {
  const {
    className,
    rating,
    count,
    countTotal,
    distribution,
    belongsTo,
    showPrompt = true,
    store,
    myReview,
    columnView = false,
    showTitles = true,
  } = props;
  const { t } = useContext(AppContext);

  const title =
    belongsTo?.type === 'product'
      ? t('reviews', 'summary.product-title')
      : t('reviews', 'summary.store-title');

  const renderEmoticon = (rate: number) => {
    const smiley = getSmileyRating(rate)?.text;
    const label = smiley ? t('reviews', smiley) : rate.toString();
    switch (rate) {
      case 1:
        return (
          <RatingIcon
            type={SmileyType.ANGRY}
            disabled={false}
            size={24}
            ariaLabel={label}
          />
        );
      case 2:
        return (
          <RatingIcon
            type={SmileyType.DISSATISFIED}
            disabled={false}
            size={24}
            ariaLabel={label}
          />
        );
      case 3:
        return (
          <RatingIcon
            type={SmileyType.NEUTRAL}
            disabled={false}
            size={24}
            ariaLabel={label}
          />
        );
      case 4:
        return (
          <RatingIcon
            type={SmileyType.SMILING}
            disabled={false}
            size={24}
            ariaLabel={label}
          />
        );
      case 5:
        return (
          <RatingIcon
            type={SmileyType.LAUGHING}
            disabled={false}
            size={24}
            ariaLabel={label}
          />
        );
      default:
        return null;
    }
  };
  const showScale = distribution && countTotal && count && count >= SCALE;

  return (
    <>
      {count && countTotal ? <S.Title>{title}</S.Title> : null}
      <S.SummaryWrapper
        className={className}
        data-test="ReviewsSummary"
        columnView={Boolean(columnView && showScale)}
      >
        <S.RatingContainer>
          <S.Summary>
            <Rating
              type={belongsTo?.type}
              rating={rating}
              count={count}
              countTotal={countTotal}
              name={belongsTo?.name}
            />
          </S.Summary>
          {belongsTo && showPrompt && (
            <S.StyledUserReviewPrompt
              firstToReview={!count && !countTotal}
              belongsTo={belongsTo}
              store={store}
              currentReview={myReview}
              control={
                <div data-test="WriteReviewButton">
                  <S.PromptButton fullWidth>
                    <ButtonIcon>
                      <IconEdit />
                    </ButtonIcon>
                    <ButtonText>
                      {t('reviews', 'button.write-review')}
                    </ButtonText>
                  </S.PromptButton>
                </div>
              }
            />
          )}
        </S.RatingContainer>
        {showScale ? (
          <S.RatingDistributionContainer>
            {showTitles && belongsTo?.type === 'store' && (
              <div>{t('reviews', 'summary.store-distribution')}</div>
            )}
            {showTitles && belongsTo?.type === 'product' && (
              <div>{t('reviews', 'summary.product-distribution')}</div>
            )}
            <S.Distribution>
              {distribution?.map((ratingCount, index) => {
                const rate = SCALE - index;
                const ratio = Math.round((ratingCount / (count || 1)) * 100);

                return (
                  <React.Fragment key={index.toString()}>
                    <S.MeterLabel>
                      {belongsTo?.type === 'store' ? (
                        renderEmoticon(rate)
                      ) : (
                        <>
                          <S.StarRate>{rate}</S.StarRate>{' '}
                          <StarIcon height={12} styling="active" />
                        </>
                      )}
                    </S.MeterLabel>
                    <S.Meter>
                      <S.Indicator width={`${ratio}%`} />
                    </S.Meter>
                    <S.MeterCount>({ratingCount})</S.MeterCount>
                  </React.Fragment>
                );
              })}
            </S.Distribution>
          </S.RatingDistributionContainer>
        ) : null}
      </S.SummaryWrapper>
    </>
  );
});

export { ReviewsSummary };
